import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '@/contexts/TranslationContext'
import { useUnit } from '@/contexts/UnitContext'
import { createUnit, getUserUnits, setCurrentUnit, getUserInvitations, acceptInvitation } from '@/lib/services/units'
import { getProfile, upsertProfile } from '@/lib/services/profiles'
import { InvitationsStep } from './InvitationsStep'
import { BillingProfileStep } from './billing/BillingProfileStep'
import { BillingWorkspaceStep } from './billing/BillingWorkspaceStep'
import { BillingCompletionStep } from './billing/BillingCompletionStep'
import type { Profile as ProfileType } from '@/lib/types'

type Step = 'loading' | 'invitations' | 'profile' | 'workspace' | 'completion'

interface BillingOnboardingData {
  profile: {
    full_name: string
    whatsapp: string
  }
  workspace: {
    name: string
    pix_key: string
  }
}

interface BillingOnboardingWizardProps {
  userId: string
}

export function BillingOnboardingWizard({ userId }: BillingOnboardingWizardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setCurrentUnit: setUnitContext } = useUnit()

  const [currentStep, setCurrentStep] = useState<Step>('loading')
  const [error, setError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)
  const [profile, setProfile] = useState<ProfileType | null>(null)
  const [invitations, setInvitations] = useState<any[]>([])
  const [data, setData] = useState<BillingOnboardingData>({
    profile: { full_name: '', whatsapp: '' },
    workspace: { name: '', pix_key: '' }
  })

  // Load profile and invitations on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load profile
        const userProfile = await getProfile(userId)
        if (userProfile) {
          setProfile(userProfile)
          setData(prev => ({
            ...prev,
            profile: {
              full_name: userProfile.full_name || '',
              whatsapp: userProfile.whatsapp || userProfile.phone || ''
            }
          }))
        }

        // Load invitations with retry (sometimes session needs a moment after login)
        let userInvitations = await getUserInvitations()

        if (!userInvitations || userInvitations.length === 0) {
          // Retry after a short delay in case session hasn't fully initialized
          await new Promise(resolve => setTimeout(resolve, 1000))
          userInvitations = await getUserInvitations()
        }

        setInvitations(userInvitations || [])

        // Determine first step
        if (userInvitations && userInvitations.length > 0) {
          setCurrentStep('invitations')
        } else {
          setCurrentStep('profile')
        }
      } catch (err) {
        console.error('[BillingOnboarding] Error loading initial data:', err)
        setError(t('onboarding.billing.errors.loadError'))
        setCurrentStep('profile')
      }
    }

    loadInitialData()
  }, [])

  const handleProfileNext = (profileData: any) => {
    setData(prev => ({
      ...prev,
      profile: profileData
    }))
    setCurrentStep('workspace')
  }

  const handleWorkspaceNext = (workspaceData: any) => {
    setData(prev => ({
      ...prev,
      workspace: workspaceData
    }))
    setCurrentStep('completion')
  }

  const handleWorkspaceBack = () => {
    setCurrentStep('profile')
  }

  const handleAcceptInvitation = async (invitationId: string) => {
    setIsAccepting(true)
    setError(null)

    try {
      await acceptInvitation(invitationId)
      console.log('[BillingOnboarding] Invitation accepted')

      // Load user units and set the accepted one as current
      const userUnits = await getUserUnits(userId)
      if (userUnits && userUnits.length > 0) {
        console.log('[BillingOnboarding] Setting first unit as current:', userUnits[0].id)
        await setUnitContext(userUnits[0].id)
        await setCurrentUnit(userId, userUnits[0].id)
        
        // Redirect directly to billing dashboard (user is now a member of a unit)
        navigate('/billing', { replace: true })
      } else {
        // Fallback: go to completion if no units found (shouldn't happen)
        setCurrentStep('completion')
      }
    } catch (err) {
      console.error('[BillingOnboarding] Error accepting invitation:', err)
      setError(err instanceof Error ? err.message : t('onboarding.billing.errors.acceptInvitationError'))
      setIsAccepting(false)
    }
  }

  const handleSkipInvitations = () => {
    setCurrentStep('profile')
  }

  const handleFinish = async () => {
    setIsCreating(true)
    setError(null)

    try {
      // 1. Upsert profile (create if not exists, update if exists)
      if (data.profile.full_name || data.profile.whatsapp) {
        await upsertProfile(userId, {
          full_name: data.profile.full_name || undefined,
          whatsapp: data.profile.whatsapp || undefined
        })
      }

      // 2. Create workspace (unit)
      const slug = data.workspace.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')

      const unit = await createUnit(data.workspace.name, slug, {
        pix_key: data.workspace.pix_key || undefined,
        context_key: 'billing'
      })

      if (!unit) {
        throw new Error(t('onboarding.billing.errors.createWorkspaceFailed'))
      }

      // 3. Set as current unit
      await setUnitContext(unit.id)
      await setCurrentUnit(userId, unit.id)

      // 4. Redirect to billing dashboard
      navigate('/billing', { replace: true })
    } catch (err) {
      console.error('[BillingOnboarding] Error finishing onboarding:', err)
      setError(err instanceof Error ? err.message : t('onboarding.billing.errors.completeError'))
      setIsCreating(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress Bar */}
        {currentStep !== 'loading' && (
          <div className="mb-12">
            <div className="flex items-center justify-center gap-4">
              {/* Show different progress bar depending on whether there are invitations */}
              {invitations.length > 0 ? (
                <>
                  {/* Step 1: Invitations */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep === 'invitations' ? 'bg-emerald-600 text-white ring-2 ring-emerald-300' :
                      currentStep === 'completion'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-emerald-200 text-emerald-700 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                      {currentStep === 'completion' ? '✓' : '1'}
                    </div>
                    <p className="text-xs mt-1 text-slate-600 dark:text-slate-400 hidden sm:block">Convites</p>
                  </div>

                  <div className={`flex-1 h-1 ${
                    currentStep === 'completion'
                      ? 'bg-emerald-600'
                      : 'bg-emerald-200 dark:bg-slate-700'
                  }`} />

                  {/* Step 2: Completion (for invitation flow) */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep === 'completion' ? 'bg-emerald-600 text-white ring-2 ring-emerald-300' :
                      'bg-emerald-200 text-emerald-700 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                      {currentStep === 'completion' ? '✓' : '2'}
                    </div>
                    <p className="text-xs mt-1 text-slate-600 dark:text-slate-400 hidden sm:block">Concluído</p>
                  </div>
                </>
              ) : (
                <>
                  {/* Step 1: Profile */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep === 'profile' ? 'bg-emerald-600 text-white ring-2 ring-emerald-300' :
                      currentStep === 'workspace' || currentStep === 'completion'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-emerald-200 text-emerald-700 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                      {currentStep === 'workspace' || currentStep === 'completion' ? '✓' : '1'}
                    </div>
                    <p className="text-xs mt-1 text-slate-600 dark:text-slate-400 hidden sm:block">Perfil</p>
                  </div>

                  <div className={`flex-1 h-1 ${
                    currentStep === 'workspace' || currentStep === 'completion'
                      ? 'bg-emerald-600'
                      : 'bg-emerald-200 dark:bg-slate-700'
                  }`} />

                  {/* Step 2: Workspace */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep === 'workspace' ? 'bg-emerald-600 text-white ring-2 ring-emerald-300' :
                      currentStep === 'completion'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-emerald-200 text-emerald-700 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                      {currentStep === 'completion' ? '✓' : '2'}
                    </div>
                    <p className="text-xs mt-1 text-slate-600 dark:text-slate-400 hidden sm:block">Área de Trabalho</p>
                  </div>

                  <div className={`flex-1 h-1 ${
                    currentStep === 'completion'
                      ? 'bg-emerald-600'
                      : 'bg-emerald-200 dark:bg-slate-700'
                  }`} />

                  {/* Step 3: Completion */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep === 'completion' ? 'bg-emerald-600 text-white ring-2 ring-emerald-300' :
                      'bg-emerald-200 text-emerald-700 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                      {currentStep === 'completion' ? '✓' : '3'}
                    </div>
                    <p className="text-xs mt-1 text-slate-600 dark:text-slate-400 hidden sm:block">Concluído</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Steps */}
        {currentStep === 'loading' && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-emerald-600 border-r-transparent" />
            <p className="mt-4 text-slate-600 dark:text-slate-400">{t('onboarding.billing.loading')}</p>
          </div>
        )}

        {currentStep === 'invitations' && (
          <InvitationsStep
            invitations={invitations}
            onAcceptInvitation={handleAcceptInvitation}
            onSkipInvitations={handleSkipInvitations}
            isAccepting={isAccepting}
          />
        )}

        {currentStep === 'profile' && (
          <BillingProfileStep
            initialData={data.profile}
            onNext={handleProfileNext}
            isLoading={isCreating}
          />
        )}

        {currentStep === 'workspace' && (
          <BillingWorkspaceStep
            initialData={data.workspace}
            userName={data.profile.full_name}
            onNext={handleWorkspaceNext}
            onBack={handleWorkspaceBack}
            isLoading={isCreating}
          />
        )}

        {currentStep === 'completion' && (
          <BillingCompletionStep
            workspaceName={data.workspace.name || (invitations[0]?.unit_name || '')}
            userName={data.profile.full_name}
            onFinish={handleFinish}
            isLoading={isCreating}
          />
        )}
      </div>
    </div>
  )
}
